import React, { useState } from 'react'
import ModalActionButton from '../../elements/ModalActionButton'
import TextField from '../../elements/form/TextField'
import { useDispatch, useSelector } from 'react-redux'
import RadioGroup from '../../elements/form/RadioGroup'
import TrashIcon from '../../elements/icons/TrashIcon'
import { getHexFromColorName } from '../../../utils'
import { createHouse } from '../../../store/actions/housingActions'

const NewHouseForm = ({close, reload}) => {
    const [housePayload, setHousePayload] = useState({
        colors: [
            {name: '', hex: '#000000'}
        ]
    })
    const [validationErrors, setValidationErrors] = useState({})

    const validateForm = () => {
        let errors = {}

        if(!housePayload.name || housePayload.name === ''){
            errors.name = true
        }
        
        if(!housePayload.gender || housePayload.gender === ''){
            errors.gender = true
        }

        if(!housePayload.colors || housePayload.colors.length === 0){
            errors.colors = true
        } else {
            housePayload.colors.forEach((color, index) => {
                if(!color.name || color.name === ''){
                    errors[`colors-${index}-name`] = true
                }
            })
        }

        setValidationErrors(errors)
        return errors
    }

    const dispatch = useDispatch()
    const housingSelector = useSelector(state => state.housing)

    const genderOptions = [
        {label: 'Male', value: 'male', description: "Create this house for male students only."},
        {label: 'Female', value: 'female', description: "Create this house for female students only."},
        {label: 'Create both', value: 'both', description: "Create this house in 2 units, one for female and one for male students."},
    ]

    const updateColor = (index, field, value) => {
        setHousePayload({
            ...housePayload,
            colors: housePayload?.colors?.map((c, i) => i === index ? {...c, [field]: value} : c)
        })
    }

    const updateColorName = (index, value) => {
        setHousePayload({
            ...housePayload,
            colors: housePayload?.colors?.map((c, i) => i === index ? {...c, name: value, hex: getHexFromColorName(value)} : c)
        })
    }

    const addColor = () => {
        setHousePayload({
            ...housePayload,
            colors: [...housePayload?.colors, {name: '', hex: '#000000'}]
        })
    }

    const removeColor = (index) => {
        setHousePayload({
            ...housePayload,
            colors: housePayload?.colors?.filter((c, i) => i !== index)
        })
    }

    const pushHouse = () => {
        if(Object.values(validateForm()).includes(true)) {
            return
        }

        dispatch(createHouse(housePayload))
    }

    return (
        <div className="w-full">
            <p className="text-sm">Please provide details of the new house below.</p>
            <div className='mt-4 w-full'>
                <TextField
                    inputLabel="Name" 
                    inputPlaceholder={`The location name`}
                    fieldId="fee-name" 
                    inputType="text" 
                    requiredField={true}
                    preloadValue={housePayload.name || ''}
                    hasError={validationErrors && validationErrors.name} 
                    returnFieldValue={(value)=>{setHousePayload({...housePayload, ...{name: value}})}}
                />
            </div>

            <div className='mt-4 w-full p-4 rounded-lg bg-gray-50 border border-gray-300'>
                <h3 className="text-sm font-semibold">House Colors</h3>
                <p className="text-sm text-gray-600">Add at least on color that represents this house</p>

                {housePayload?.colors?.map((color, index) => (
                    <div className="relative mb-4" key={index}>
                        {index > 0 && <button onClick={()=>{removeColor(index)}} className='absolute z-50 top-1 right-0 flex items-center gap-x-2 text-red-600 hover:text-red-800 duration-200 transition text-xs'>
                            <TrashIcon className={`w-5 h-5`} />
                        </button>}
                        <div className="flex items-start gap-x-2">
                            <div className="w-full">
                                <TextField
                                    inputLabel="Color name" 
                                    inputPlaceholder={`The color name (e.g. green)`}
                                    fieldId="fee-name" 
                                    inputType="text" 
                                    requiredField={true}
                                    preloadValue={housePayload?.colors?.[index]?.name || ''}
                                    hasError={validationErrors && validationErrors[`colors-${index}-name`]} 
                                    returnFieldValue={(value)=>{updateColorName(index, value)}}
                                />
                            </div>
                            <div className=''>
                                <label 
                                    className={`text-sm lg:text-md cursor-text z-10 relative py-1 transition mb-2 block duration-200  
                                    ${validationErrors && validationErrors[`colors-${index}-name`] ? 'text-red-600' : 'text-gray-500'}`}
                                >
                                <span className='text-red-600'>*</span>   Color
                                </label>

                                <input 
                                    type="color"
                                    className={`rounded h-[45px] py-1 px-1 text-sm block w-[100px] focus:border-gray-800 focus:outline-none hover:border-gray-200 hover:bg-gray-50 border bg-gray-100 transition duration-200 focus:bg-white font-outfit placeholder:font-outfit  ${validationErrors && validationErrors[`colors-${index}-name`] ? 'border-red-600' : 'border-gray-100'}`} 
                                    value={housePayload?.colors?.[index]?.hex || '#000000'}
                                    onChange={(e)=>{updateColor(index, 'hex', e.target.value)}}

                                />
                            </div>

                        </div>
                    </div>
                ))}
                <button onClick={addColor} className='w-full p-3 text-sm border border-dashed border-gray-500 transition duration-200 text-gray-800 rounded-lg mt-2 font-space-grotesk font-semibold'>Add another color</button>
            </div>

            <div className='mt-4 w-full'>
                <RadioGroup 
                    items={genderOptions} 
                    inline={false}
                    preSelectedIndex={genderOptions.findIndex(item => item.value === housePayload.gender)}
                    inputLabel={'House Gender'}
                    requiredField={true}
                    returnSelected={(value) => {setHousePayload({...housePayload, ...{gender: value.value}})}} 
                    hasError={validationErrors && validationErrors.gender}  
                    titleField='label'
                />
            </div>

            <div className="mt-8 flex flex-row-reverse gap-x-4">
                <ModalActionButton
                    buttonLabel={`Create new house`}
                    buttonAction={()=>{pushHouse()}}
                    processing={housingSelector?.creatingHouse}
                />

                <button
                    type="button"
                    className="inline-flex justify-center px-5 py-4 text-xs font-medium bg-transparent border border-transparent rounded-md hover:bg-opacity-50 hover:bg-secondary focus:outline-none"
                    onClick={close}
                >
                    Cancel
                </button>
            
            </div>
        </div>
    )
}

export default NewHouseForm